import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import { useFirstAidDiscrepancies } from "../../../query/hooks/QueryHooksFirstAidBox";
import { CONDITION_LABELS, OHS_BASE_PATH } from "./ohsConstants";
import { ConditionBadge, MutedText } from "./Ohs.styles";
import { getItemStatuses } from "./ohsUtils";

const EXPORT_HEADER = ["Box", "Campus", "Item", "First Aider Condition", "First Aider Qty", "OHS Rep Condition", "OHS Rep Qty"];

/**
 * Renders one side of the comparison - condition, quantity, and who checked it when.
 * @param {Object} checkSummary { condition, quantity, check_date, checked_by_name }
 * @returns {JSX.Element}
 */
function renderCheckSummary(checkSummary) {
    return (
        <div>
            {getItemStatuses(checkSummary).map((itemStatus) => (
                <ConditionBadge key={itemStatus} $condition={itemStatus}>{CONDITION_LABELS[itemStatus]}</ConditionBadge>
            ))}
            {" "}qty {checkSummary.quantity}
            <div>
                <MutedText>{moment(checkSummary.check_date).format(TABLE_DATE_FORMAT)} · {checkSummary.checked_by_name}</MutedText>
            </div>
        </div>
    );
}

/**
 * Discrepancies - items where a manager's latest manager check disagrees with the first aider's
 * latest check, to catch under- or over-reporting.
 */
function Discrepancies() {
    const { data: discrepancies, isLoading } = useFirstAidDiscrepancies();
    const [search, setSearch] = useState("");

    const discrepancyRows = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        return discrepancies
            .map((discrepancy) => ({ ...discrepancy, rowKey: `${discrepancy.box_id}-${discrepancy.box_item_id}` }))
            .filter((discrepancy) => {
                return !searchText
                    || discrepancy.box_code.toLowerCase().includes(searchText)
                    || discrepancy.item_name.toLowerCase().includes(searchText)
                    || (discrepancy.campus_name || "").toLowerCase().includes(searchText);
            });
    }, [discrepancies, search]);

    const columns = useMemo(
        () => [
            {
                name: "Box",
                selector: "box_code",
                sortable: true,
                minWidth: "160px",
                cell: (row) => (
                    <div>
                        <Link to={`${OHS_BASE_PATH}/boxes/open/${row.box_id}`}>{row.box_code}</Link>
                        <div><MutedText>{row.campus_name} · {row.building_name}</MutedText></div>
                    </div>
                ),
            },
            {
                name: "Item",
                selector: "item_name",
                sortable: true,
                grow: 2,
                minWidth: "200px",
            },
            {
                name: "First Aider Reported",
                minWidth: "200px",
                cell: (row) => renderCheckSummary(row.firstAider),
            },
            {
                name: "OHS Rep Found",
                minWidth: "200px",
                cell: (row) => renderCheckSummary(row.manager),
            },
            {
                name: "Difference",
                selector: "quantityDifference",
                sortable: true,
                minWidth: "180px",
                cell: (row) => {
                    if (row.quantityDifference > 0) {
                        return `First aider reported ${row.quantityDifference} more`;
                    }
                    if (row.quantityDifference < 0) {
                        return `First aider reported ${Math.abs(row.quantityDifference)} fewer`;
                    }
                    return "Condition only";
                },
            },
        ],
        []
    );

    /**
     * Rows for the CSV/XLSX/PDF export.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = discrepancyRows.map((discrepancy) => [
            discrepancy.box_code,
            discrepancy.campus_name,
            discrepancy.item_name,
            getItemStatuses(discrepancy.firstAider).map((itemStatus) => CONDITION_LABELS[itemStatus]).join(", "),
            discrepancy.firstAider.quantity,
            getItemStatuses(discrepancy.manager).map((itemStatus) => CONDITION_LABELS[itemStatus]).join(", "),
            discrepancy.manager.quantity,
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <>
            <MutedText>
                Compares each box&apos;s latest First Aider Check with its latest OHS Rep Check. Use Perform OHS Rep Check on a box to add it here.
            </MutedText>
            <DataTableComponentFrontPagination
                data={discrepancyRows}
                columns={columns}
                loading={isLoading}
                keyField="rowKey"
                search={search}
                setSearch={setSearch}
                exportData={exportData}
                exportFileName="First_Aid_Discrepancies"
                defaultSort={{ defaultSortColumn: "box_code", defaultSortAsc: true }}
            />
        </>
    );
}

export default Discrepancies;
